import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Trash2, 
  Pencil, 
  FolderOpen, 
  ToggleLeft, 
  ToggleRight, 
  GripVertical,
  Eye,
  EyeOff,
  Package,
  Leaf,
  Pill,
  Sparkles,
  Briefcase,
  Coffee,
  Heart,
  Star,
  Zap,
  Sun,
  Moon
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Leaf: Leaf,
  Pill: Pill,
  Sparkles: Sparkles,
  Briefcase: Briefcase,
  Coffee: Coffee,
  Heart: Heart,
  Star: Star,
  Zap: Zap,
  Sun: Sun,
  Moon: Moon,
  Package: Package
};

const iconOptions = Object.keys(iconMap);

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    image_url: '',
    icon: 'Package',
    display_order: 0,
    is_active: true,
    show_in_nav: true,
    hero_title: '',
    hero_subtitle: ''
  });

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?active_only=false`);
      setCategories(response.data);
    } catch (error) {
      // If no categories exist, try to migrate
      if (error.response?.status === 404 || categories.length === 0) {
        try {
          await axios.post(`${API}/migrate/categories`);
          const retryResponse = await axios.get(`${API}/categories?active_only=false`);
          setCategories(retryResponse.data);
        } catch (migrateError) {
          // Migration failed - categories will remain empty
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      image_url: '',
      icon: 'Package',
      display_order: categories.length,
      is_active: true,
      show_in_nav: true,
      hero_title: '',
      hero_subtitle: ''
    });
    setEditingCategory(null);
  };

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        image_url: category.image_url || '',
        icon: category.icon || 'Package',
        display_order: category.display_order || 0,
        is_active: category.is_active !== false,
        show_in_nav: category.show_in_nav !== false,
        hero_title: category.hero_title || '',
        hero_subtitle: category.hero_subtitle || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: editingCategory ? prev.slug : generateSlug(name),
      hero_title: prev.hero_title || name
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.slug) {
      toast.error('Name and slug are required');
      return;
    }

    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, formData);
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/admin/categories`, formData);
        toast.success('Category created');
      }
      setShowModal(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDelete = async (id, name, productCount) => {
    if (productCount > 0) {
      toast.error(`Cannot delete "${name}" - it has ${productCount} products. Move or delete products first.`);
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${id}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await axios.put(`${API}/admin/categories/${id}/toggle`);
      toast.success(response.data.message);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to toggle category');
    }
  };

  const IconComponent = ({ iconName, className }) => {
    const Icon = iconMap[iconName] || Package;
    return <Icon className={className} />;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 w-48 bg-sand rounded" />
        <div className="h-64 bg-sand rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="category-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl text-text-primary">Categories</h2>
          <p className="text-sm text-text-muted">Manage your product categories</p>
        </div>
        <Button onClick={() => openModal()} className="rounded-full bg-forest hover:bg-forest-dark text-white">
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </Button>
      </div>

      {categories.length === 0 ? (
        <div className="text-center py-12 bg-sand/30 rounded-2xl">
          <FolderOpen className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-muted">No categories yet</p>
          <Button onClick={() => openModal()} className="mt-4 rounded-full" variant="outline">
            Create your first category
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-sand">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Order</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Category</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Slug</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Products</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Nav</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {categories.map((category) => (
                <tr key={category.id} className={!category.is_active ? 'opacity-50' : ''}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-text-muted" />
                      <span className="text-text-muted">{category.display_order}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-terracotta/10 flex items-center justify-center">
                        <IconComponent iconName={category.icon} className="w-5 h-5 text-terracotta" />
                      </div>
                      <div>
                        <span className="font-medium text-text-primary">{category.name}</span>
                        {category.description && (
                          <p className="text-xs text-text-muted line-clamp-1">{category.description}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-sm bg-sand px-2 py-1 rounded">{category.slug}</code>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-text-primary">{category.product_count || 0}</span>
                  </td>
                  <td className="px-4 py-3">
                    {category.show_in_nav ? (
                      <Eye className="w-4 h-4 text-forest" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-text-muted" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggle(category.id)}
                      className="flex items-center gap-1"
                    >
                      {category.is_active ? (
                        <>
                          <ToggleRight className="w-6 h-6 text-forest" />
                          <span className="text-xs text-forest">Active</span>
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="w-6 h-6 text-text-muted" />
                          <span className="text-xs text-text-muted">Inactive</span>
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openModal(category)}
                        className="p-2 text-text-muted hover:text-terracotta hover:bg-terracotta/10 rounded-lg transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id, category.name, category.product_count)}
                        className="p-2 text-text-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingCategory ? 'Edit Category' : 'Create Category'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={handleNameChange}
                  placeholder="e.g., Wellness Kits"
                  className="rounded-xl mt-1"
                  required
                />
              </div>
              <div>
                <Label>Slug *</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
                  placeholder="e.g., wellness-kits"
                  className="rounded-xl mt-1 font-mono text-sm"
                  required
                />
                <p className="text-xs text-text-muted mt-1">URL: /{formData.slug || 'category-slug'}</p>
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this category"
                className="rounded-xl mt-1"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Icon</Label>
                <Select
                  value={formData.icon}
                  onValueChange={(value) => setFormData({ ...formData, icon: value })}
                >
                  <SelectTrigger className="rounded-xl mt-1">
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        <IconComponent iconName={formData.icon} className="w-4 h-4" />
                        {formData.icon}
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map((icon) => (
                      <SelectItem key={icon} value={icon}>
                        <div className="flex items-center gap-2">
                          <IconComponent iconName={icon} className="w-4 h-4" />
                          {icon}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  className="rounded-xl mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Image URL (optional)</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
                className="rounded-xl mt-1"
              />
            </div>

            <div className="border-t border-stone-200 pt-4">
              <h4 className="font-medium text-text-primary mb-3">Page Hero Section</h4>
              <div className="space-y-3">
                <div>
                  <Label>Hero Title</Label>
                  <Input
                    value={formData.hero_title}
                    onChange={(e) => setFormData({ ...formData, hero_title: e.target.value })}
                    placeholder={formData.name || "Category name"}
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Hero Subtitle</Label>
                  <Input
                    value={formData.hero_subtitle}
                    onChange={(e) => setFormData({ ...formData, hero_subtitle: e.target.value })}
                    placeholder="Tagline or description"
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-stone-300"
                />
                <span className="text-sm text-text-secondary">Active (visible to customers)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.show_in_nav}
                  onChange={(e) => setFormData({ ...formData, show_in_nav: e.target.checked })}
                  className="rounded border-stone-300"
                />
                <span className="text-sm text-text-secondary">Show in navigation</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-full">
                Cancel
              </Button>
              <Button type="submit" className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white">
                {editingCategory ? 'Update' : 'Create'} Category
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoryManager;
